import {
    Color3,
    Constants,
    Mesh,
    ParticleSystem,
    Vector3,
    Vector4,
    VertexData,
    type AbstractMesh,
    type BoxParticleEmitter,
    type Scene,
    type ShaderMaterial,
    type TransformNode,
} from '@babylonjs/core';
import { getGlowHaloTexture } from '../procedural-textures';
import { registerHeldDoorShaders } from './held-door-shaders';
import type { VfxWarmup } from './vfx-warmup';
import { createVfxShaderMaterial } from './vfx-shader-material';
import { applyParticleTint } from './particle-sprites';

/** A doorway The Held Door hangs its veil across. */
export interface HeldDoorway {
    /** One jamb, at floor level, in scene space. */
    readonly from: Vector3;
    /** The other jamb, at floor level. */
    readonly to: Vector3;
    readonly heightMeters: number;
    readonly colorHex: string;
}

/** An enemy let through The Held Door. Its body is taken to stand on the doorway's floor. */
export interface HeldDoorCrosser {
    /** The enemy's root: followed every frame (its absolute position on the ground). */
    readonly root: TransformNode;
    readonly radius: number;
    readonly height: number;
}

/** The veil fading in as a faint outline once a hold starts. */
const OUTLINE_FADE_IN_SECONDS = 0.2;
/** The outline filling into the full veil as it is raised, with a brief brightening. */
const RAISE_SECONDS = 0.3;
const RAISE_FLASH = 0.6;
/** The veil dissolving away, top first, when its time is up or it is released. */
const DISSOLVE_SECONDS = 0.7;
/** The veil's grid: one vertex about every this many meters, so the bulges and ripples bend smoothly. */
const GRID_SPACING = 0.06;
/** Time left after the veil is gone for its last motes to die out before it is cleaned up. */
const LINGER_SECONDS = 1;

/** The skin clings this far past the crosser's back before it lets go and snaps back. */
const CLING_PAST_METERS = 0.25;
/** The edge glow rising as the body first touches the skin, and fading once it is through. */
const EDGE_RISE_METERS = 0.15;
const EDGE_FADE_SECONDS = 0.15;
/** The snap back: a damped spring, so the skin overshoots and wobbles before it settles flat. */
const SNAP_STIFFNESS = 140;
const SNAP_DAMPING = 10;
const SETTLED_METERS = 0.004;
/** A crosser still not through after this long (it stopped, or turned back) is let go anyway. */
const CROSS_TIMEOUT_SECONDS = 4;
/** The ripples spreading from where it broke through; the shader fades them out well within this. */
const WAKE_SECONDS = 1.8;
/** Where up the body the wake centers, as a share of its height. */
const WAKE_HEIGHT = 0.55;
/** Drips fall off the crosser once through, thinning out over this long. */
const DRIP_SECONDS = 0.8;
const DRIP_RATE = 45;
/** Time left after the last drip falls for it to land before it is cleaned up. */
const DRIP_LINGER_SECONDS = 0.7;
/** Where an unused crosser slot is parked: far outside any doorway, not pushing. */
const UNUSED_CROSSER = new Vector4(1000, 0.3, 1.8, 0);
/** An unused wake slot: long faded, with no strength. */
const UNUSED_WAKE = new Vector4(1000, 0, 100, 0);

type CrosserSlot = 'A' | 'B';
type WakeSlot = 'wakeA' | 'wakeB';

interface Crossing {
    readonly crosser: HeldDoorCrosser;
    /** Which way it crosses, along the veil's local z: +1 or -1. */
    readonly direction: number;
    /** The bulge it drives, until the skin has settled; null once given up (settled, or taken by a newer crossing). */
    slot: CrosserSlot | null;
    /** Where along the doorway, in meters from its middle; held where it broke through once it has. */
    x: number;
    /** How far the skin is pushed, in meters along `direction`, and how fast it is springing back. */
    bulge: number;
    bulgeSpeed: number;
    edge: number;
    age: number;
    /** Seconds since it broke through; null while it is still pushing in. */
    sinceThrough: number | null;
    drips: ParticleSystem | null;
    /** Where the drips fall from: the crosser's feet, moved with it each frame. */
    readonly dripOrigin: Vector3;
}

interface Wake {
    readonly slot: WakeSlot;
    readonly x: number;
    readonly y: number;
    age: number;
}

interface HeldDoor {
    readonly doorway: HeldDoorway;
    readonly mesh: Mesh;
    readonly material: ShaderMaterial;
    readonly motes: ParticleSystem;
    readonly moteBox: BoxParticleEmitter;
    readonly color: Color3;
    /** The doorway's middle, at floor level. */
    readonly center: Vector3;
    /** The doorway's direction, jamb to jamb, along the ground. */
    readonly axis: Vector3;
    /** The veil's local z in scene space: square to the doorway, along the ground. */
    readonly normal: Vector3;
    readonly crossings: Crossing[];
    readonly wakes: Wake[];
    elapsed: number;
    /** When it was raised (`elapsed`), or null while it is only a held outline. */
    raisedAt: number | null;
    /** When it starts dissolving; Infinity while held with no end in sight. */
    endsAt: number;
}

/**
 * Draws The Held Door (docs/gameplay/combat-vfx.md): while the hotkey is
 * held, a faint outline of the veil previews where it will hang; raised, a
 * translucent rippling liquid skin hangs across the doorway, replacing the
 * flat line telegraph; when its time is up it dissolves from the top down.
 * Enemies waiting at the door get no cue.
 *
 * Each enemy let through pushes into the skin: it bulges ahead of them
 * around their shape and clings over them, with a glowing edge where the
 * body meets it; once they are through it snaps back with a wobble and a
 * wake of ripples, and the veil drips off them for a moment as they walk
 * on. The system follows each crosser's mesh itself and
 * ends the crossing once it is clear, so callers only say who is let
 * through. Two crossings can push at once.
 *
 * The veil is one procedural shader on an upright grid (see
 * `held-door-shaders.ts`); the motes it sheds as it dissolves and the drips are
 * Babylon particles - client visuals need no `SeededRng`.
 */
export class HeldDoorSystem {
    private readonly doors = new Map<string, HeldDoor>();
    private timeSeconds = 0;
    private doorCount = 0;
    private crossingCount = 0;

    constructor(private readonly scene: Scene) {
        registerHeldDoorShaders();
    }

    /** Readies this system's shaders and textures before its first effect, so it shows at once (`VfxWarmup`). */
    warm(warmup: VfxWarmup): void {
        const color = Color3.White();
        warmup.material(this.createVeilMaterial('held-door-warmup-veil', color, 1, 1));
        warmup.particleSystem(this.createMotes('held-door-warmup-motes', Vector3.Zero(), 1, color).motes);
        warmup.particleSystem(this.createDrips('held-door-warmup-drips', Vector3.Zero(), { radius: 0.5, height: 1.8 }, color));
    }

    /** The hotkey is being held, aimed at `doorway`: its faint outline. Aiming at a different doorway moves it. */
    hold(id: string, doorway: HeldDoorway): void {
        const existing = this.doors.get(id);
        if (existing && sameDoorway(existing.doorway, doorway)) {
            return;
        }
        if (existing) {
            this.disposeDoor(existing);
        }
        this.doors.set(id, this.createDoor(doorway));
    }

    /** The door is held: the veil hangs for `durationSeconds`, then dissolves. Raises the held outline if there is one. */
    raise(id: string, doorway: HeldDoorway, durationSeconds: number): void {
        let door = this.doors.get(id);
        if (!door || !sameDoorway(door.doorway, doorway) || door.raisedAt !== null) {
            if (door) {
                this.disposeDoor(door);
            }
            door = this.createDoor(doorway);
            this.doors.set(id, door);
        }
        door.raisedAt = door.elapsed;
        door.endsAt = door.elapsed + durationSeconds;
    }

    /**
     * An enemy is let through: from here on the skin follows `crosser`'s mesh,
     * bulging around it as it pushes in, and snapping back once it is through.
     * It crosses away from the side it stands on now. The crossing ends by
     * itself once the enemy is clear (or after a few seconds if it never is).
     */
    cross(id: string, crosser: HeldDoorCrosser): void {
        const door = this.doors.get(id);
        if (!door || door.raisedAt === null || door.elapsed >= door.endsAt || crosser.root.isDisposed()) {
            return;
        }
        if (door.crossings.some((crossing) => crossing.crosser.root === crosser.root && crossing.sinceThrough === null)) {
            return;
        }
        const { x, depth } = this.toDoorLocal(door, crosser.root);
        const crossing: Crossing = {
            crosser,
            direction: depth > 0 ? -1 : 1,
            slot: this.takeCrosserSlot(door),
            x,
            bulge: 0,
            bulgeSpeed: 0,
            edge: 0,
            age: 0,
            sinceThrough: null,
            drips: null,
            dripOrigin: new Vector3(),
        };
        door.crossings.push(crossing);
    }

    /** The hold was let go without raising it, or the door ends early: it dissolves now. */
    release(id: string): void {
        const door = this.doors.get(id);
        if (door && door.elapsed < door.endsAt) {
            door.endsAt = door.elapsed;
        }
    }

    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        for (const [id, door] of this.doors) {
            door.elapsed += deltaSeconds;
            if (door.elapsed >= door.endsAt + DISSOLVE_SECONDS + LINGER_SECONDS && door.crossings.length === 0) {
                this.disposeDoor(door);
                this.doors.delete(id);
                continue;
            }
            this.applyFrame(door, deltaSeconds);
        }
    }

    dispose(): void {
        this.doors.forEach((door) => this.disposeDoor(door));
        this.doors.clear();
    }

    private createDoor(doorway: HeldDoorway): HeldDoor {
        const name = `held-door-${this.doorCount++}`;
        const span = doorway.to.subtract(doorway.from);
        span.y = 0;
        const width = Math.max(0.1, span.length());
        const axis = span.normalize();
        const color = Color3.FromHexString(doorway.colorHex);

        const mesh = this.createVeilMesh(name, width, doorway.heightMeters);
        const center = Vector3.Center(doorway.from, doorway.to);
        center.y = Math.min(doorway.from.y, doorway.to.y);
        mesh.position.copyFrom(center);
        // Turns the grid's x axis to run from one jamb to the other; its z axis then runs along `normal`.
        mesh.rotation.y = Math.atan2(-axis.z, axis.x);

        const material = this.createVeilMaterial(name, color, width, doorway.heightMeters);
        mesh.material = material;

        const { motes, moteBox } = this.createMotes(`${name}-motes`, mesh, width, color);
        const door: HeldDoor = {
            doorway,
            mesh,
            material,
            motes,
            moteBox,
            color,
            center,
            axis,
            normal: new Vector3(-axis.z, 0, axis.x),
            crossings: [],
            wakes: [],
            elapsed: 0,
            raisedAt: null,
            endsAt: Infinity,
        };
        this.applyFrame(door, 0);
        return door;
    }

    private applyFrame(door: HeldDoor, deltaSeconds: number): void {
        const { elapsed, material, doorway } = door;
        material.setFloat('time', this.timeSeconds);

        const fadeIn = Math.min(1, elapsed / OUTLINE_FADE_IN_SECONDS);
        let outline = 1;
        let intensity = fadeIn * (0.8 + (0.2 * Math.sin(this.timeSeconds * 4)));
        if (door.raisedAt !== null) {
            const raise = Math.min(1, (elapsed - door.raisedAt) / RAISE_SECONDS);
            outline = 1 - raise;
            intensity = 1 + (RAISE_FLASH * Math.sin(raise * Math.PI));
        }
        material.setFloat('outline', outline);
        material.setFloat('intensity', intensity);

        const dissolve = Math.min(1, Math.max(0, (elapsed - door.endsAt) / DISSOLVE_SECONDS));
        material.setFloat('dissolve', dissolve);
        door.mesh.setEnabled(dissolve < 1);
        // Motes shed along the dissolving edge (the shader's `line`) as it sinks.
        const dissolving = dissolve > 0 && dissolve < 1 && door.raisedAt !== null;
        door.moteBox.minEmitBox.y = doorway.heightMeters * (1 - (dissolve * 1.1));
        door.moteBox.maxEmitBox.y = door.moteBox.minEmitBox.y;
        if (dissolving && !door.motes.isStarted()) {
            door.motes.start();
        } else if (!dissolving && door.motes.isStarted()) {
            door.motes.stop();
        }

        for (let index = door.crossings.length - 1; index >= 0; index -= 1) {
            const crossing = door.crossings[index]!;
            if (!this.stepCrossing(door, crossing, deltaSeconds)) {
                this.disposeCrossing(door, crossing);
                door.crossings.splice(index, 1);
            }
        }

        for (let index = door.wakes.length - 1; index >= 0; index -= 1) {
            const wake = door.wakes[index]!;
            wake.age += deltaSeconds;
            if (wake.age >= WAKE_SECONDS) {
                material.setVector4(wake.slot, UNUSED_WAKE);
                door.wakes.splice(index, 1);
                continue;
            }
            material.setVector4(wake.slot, new Vector4(wake.x, wake.y, wake.age, 1));
        }
    }

    /** Moves one crossing on a frame; false once it is over and can be cleaned up. */
    private stepCrossing(door: HeldDoor, crossing: Crossing, deltaSeconds: number): boolean {
        const { crosser } = crossing;
        if (crosser.root.isDisposed()) {
            return false;
        }
        crossing.age += deltaSeconds;
        const { x, depth } = this.toDoorLocal(door, crosser.root);

        if (crossing.sinceThrough === null) {
            crossing.x = x;
            // How far the body's middle is past the veil, along the way it crosses.
            const past = depth * crossing.direction;
            // The skin rests on the body's leading side, stretching until it is well past its back.
            const reach = past + crosser.radius;
            const clingMax = (2 * crosser.radius) + CLING_PAST_METERS;
            crossing.bulge = Math.max(0, Math.min(clingMax, reach));
            crossing.edge = Math.max(0, Math.min(1, reach / EDGE_RISE_METERS));
            const ended = door.elapsed >= door.endsAt;
            if (reach >= clingMax || crossing.age >= CROSS_TIMEOUT_SECONDS || ended) {
                // Only a body that got its middle through drips; one that stopped short or turned back does not.
                this.breakThrough(door, crossing, past >= 0);
            }
        } else {
            crossing.sinceThrough += deltaSeconds;
            crossing.edge = Math.max(0, 1 - (crossing.sinceThrough / EDGE_FADE_SECONDS));
            this.springBack(crossing, deltaSeconds);
            if (crossing.slot && crossing.sinceThrough > EDGE_FADE_SECONDS
                && Math.abs(crossing.bulge) < SETTLED_METERS && Math.abs(crossing.bulgeSpeed) < SETTLED_METERS * 10) {
                this.freeCrosserSlot(door, crossing);
            }
            const dripping = Math.max(0, 1 - (crossing.sinceThrough / DRIP_SECONDS));
            if (crossing.drips) {
                const position = currentPosition(crosser.root);
                crossing.dripOrigin.set(position.x, door.center.y, position.z);
                crossing.drips.emitRate = DRIP_RATE * dripping;
                if (dripping <= 0 && crossing.drips.isStarted()) {
                    crossing.drips.stop();
                }
            }
            if (crossing.sinceThrough >= DRIP_SECONDS + DRIP_LINGER_SECONDS && !crossing.slot) {
                return false;
            }
        }

        if (crossing.slot) {
            door.material.setVector4(
                `crosser${crossing.slot}`,
                new Vector4(crossing.x, crosser.radius, crosser.height, crossing.bulge * crossing.direction),
            );
            door.material.setFloat(`edge${crossing.slot}`, crossing.edge);
        }
        return true;
    }

    /** The crosser is through: the skin lets go, a wake spreads from where it was, and (if `coated`) the veil drips off them. */
    private breakThrough(door: HeldDoor, crossing: Crossing, coated: boolean): void {
        crossing.sinceThrough = 0;
        const { crosser } = crossing;
        if (crossing.bulge > 0) {
            this.startWake(door, crossing.x, crosser.height * WAKE_HEIGHT);
        }
        if (!coated) {
            return;
        }
        const name = `${door.mesh.name}-crossing-${this.crossingCount++}`;
        const position = currentPosition(crosser.root);
        crossing.dripOrigin.set(position.x, door.center.y, position.z);
        crossing.drips = this.createDrips(`${name}-drips`, crossing.dripOrigin, crosser, door.color);
        crossing.drips.start();
    }

    /** The skin's bulge springing back to flat: semi-implicit steps, short enough to stay stable on a slow frame. */
    private springBack(crossing: Crossing, deltaSeconds: number): void {
        const steps = Math.max(1, Math.ceil(deltaSeconds * 120));
        const step = deltaSeconds / steps;
        for (let index = 0; index < steps; index += 1) {
            crossing.bulgeSpeed += ((-SNAP_STIFFNESS * crossing.bulge) - (SNAP_DAMPING * crossing.bulgeSpeed)) * step;
            crossing.bulge += crossing.bulgeSpeed * step;
        }
    }

    private startWake(door: HeldDoor, x: number, y: number): void {
        const used = new Set(door.wakes.map((wake) => wake.slot));
        let slot: WakeSlot | undefined = (['wakeA', 'wakeB'] as const).find((candidate) => !used.has(candidate));
        if (!slot) {
            // Both in use: the oldest wake gives way.
            const oldest = door.wakes.reduce((a, b) => (a.age >= b.age ? a : b));
            door.wakes.splice(door.wakes.indexOf(oldest), 1);
            slot = oldest.slot;
        }
        door.wakes.push({ slot, x, y, age: 0 });
    }

    /** A free crosser slot, or the oldest crossing's if both are in use: that crossing's bulge gives way. */
    private takeCrosserSlot(door: HeldDoor): CrosserSlot {
        const holding = door.crossings.filter((crossing) => crossing.slot !== null);
        const used = new Set(holding.map((crossing) => crossing.slot));
        const free = (['A', 'B'] as const).find((candidate) => !used.has(candidate));
        if (free) {
            return free;
        }
        const oldest = holding.reduce((a, b) => (a.age >= b.age ? a : b));
        const slot = oldest.slot!;
        oldest.slot = null;
        return slot;
    }

    private freeCrosserSlot(door: HeldDoor, crossing: Crossing): void {
        if (!crossing.slot) {
            return;
        }
        door.material.setVector4(`crosser${crossing.slot}`, UNUSED_CROSSER);
        door.material.setFloat(`edge${crossing.slot}`, 0);
        crossing.slot = null;
    }

    /** Where `mesh` stands relative to the doorway: `x` along it from its middle, `depth` out from the veil along its local z. */
    private toDoorLocal(door: HeldDoor, node: TransformNode): { x: number; depth: number } {
        const offset = currentPosition(node).subtract(door.center);
        return { x: Vector3.Dot(offset, door.axis), depth: Vector3.Dot(offset, door.normal) };
    }

    /** An upright grid from one jamb to the other (x centered on the doorway's middle), from the floor up to `height`. */
    private createVeilMesh(name: string, width: number, height: number): Mesh {
        const columns = Math.max(2, Math.ceil(width / GRID_SPACING));
        const rows = Math.max(2, Math.ceil(height / GRID_SPACING));
        const positions: number[] = [];
        const uvs: number[] = [];
        const indices: number[] = [];
        for (let row = 0; row <= rows; row += 1) {
            for (let column = 0; column <= columns; column += 1) {
                const u = column / columns;
                const v = row / rows;
                positions.push((u - 0.5) * width, v * height, 0);
                uvs.push(u, v);
                if (row < rows && column < columns) {
                    const base = (row * (columns + 1)) + column;
                    const above = base + columns + 1;
                    indices.push(base, above, base + 1, base + 1, above, above + 1);
                }
            }
        }
        const vertexData = new VertexData();
        vertexData.positions = positions;
        vertexData.uvs = uvs;
        vertexData.indices = indices;
        const mesh = new Mesh(name, this.scene);
        vertexData.applyToMesh(mesh, false);
        mesh.isPickable = false;
        // The bulges and ripples push the flat grid out of its bounding box, so it is never culled by it.
        mesh.alwaysSelectAsActiveMesh = true;
        return mesh;
    }

    /** The veil's skin (`heldDoorVeil`), sized to its doorway, with no crosser or wake on it. */
    private createVeilMaterial(name: string, color: Color3, width: number, height: number): ShaderMaterial {
        const material = createVfxShaderMaterial(this.scene, name, 'heldDoorVeil', {
            uniforms: [
                'worldViewProjection', 'time', 'intensity', 'outline', 'dissolve', 'widthMeters', 'heightMeters',
                'crosserA', 'crosserB', 'edgeA', 'edgeB', 'wakeA', 'wakeB', 'veilColor',
            ],
            alphaMode: Constants.ALPHA_ADD,
        });
        material.setColor3('veilColor', color);
        material.setFloat('widthMeters', width);
        material.setFloat('heightMeters', height);
        for (const slot of ['A', 'B'] as const) {
            material.setVector4(`crosser${slot}`, UNUSED_CROSSER);
            material.setFloat(`edge${slot}`, 0);
        }
        material.setVector4('wakeA', UNUSED_WAKE);
        material.setVector4('wakeB', UNUSED_WAKE);
        return material;
    }

    /** Motes shaken loose along the dissolving edge, drifting down as they fade. Emitted in the veil's own space. */
    private createMotes(name: string, veil: AbstractMesh | Vector3, width: number, color: Color3): { motes: ParticleSystem; moteBox: BoxParticleEmitter } {
        const motes = new ParticleSystem(name, 160, this.scene);
        motes.particleTexture = getGlowHaloTexture(this.scene);
        motes.emitter = veil;
        const moteBox = motes.createBoxEmitter(
            new Vector3(-0.2, -0.3, -0.2),
            new Vector3(0.2, 0.1, 0.2),
            new Vector3(-width / 2, 0, 0),
            new Vector3(width / 2, 0, 0),
        );
        motes.emitRate = 70 * width;
        motes.minLifeTime = 0.5;
        motes.maxLifeTime = 1;
        motes.minSize = 0.08;
        motes.maxSize = 0.15;
        motes.minEmitPower = 0.2;
        motes.maxEmitPower = 0.5;
        motes.gravity = new Vector3(0, -0.8, 0);
        applyParticleTint(motes, color, 0.45);
        motes.blendMode = ParticleSystem.BLENDMODE_ADD;
        return { motes, moteBox };
    }

    /** Drops of the veil running off the crosser's body and falling, left behind as it walks on. `origin` is its feet. */
    private createDrips(name: string, origin: Vector3, crosser: Pick<HeldDoorCrosser, 'radius' | 'height'>, color: Color3): ParticleSystem {
        const drips = new ParticleSystem(name, 60, this.scene);
        drips.particleTexture = getGlowHaloTexture(this.scene);
        drips.emitter = origin;
        const reach = crosser.radius * 1.05;
        drips.createBoxEmitter(
            new Vector3(-0.1, -0.4, -0.1),
            new Vector3(0.1, -0.1, 0.1),
            new Vector3(-reach, crosser.height * 0.3, -reach),
            new Vector3(reach, crosser.height * 0.9, reach),
        );
        drips.emitRate = DRIP_RATE;
        drips.minLifeTime = 0.35;
        drips.maxLifeTime = 0.6;
        drips.minSize = 0.05;
        drips.maxSize = 0.09;
        drips.minEmitPower = 0.3;
        drips.maxEmitPower = 0.8;
        drips.gravity = new Vector3(0, -7, 0);
        applyParticleTint(drips, color, 0.4);
        drips.blendMode = ParticleSystem.BLENDMODE_ADD;
        return drips;
    }

    private disposeCrossing(door: HeldDoor, crossing: Crossing): void {
        this.freeCrosserSlot(door, crossing);
        // The particle texture is the shared glow halo, so it is kept.
        crossing.drips?.dispose(false);
    }

    private disposeDoor(door: HeldDoor): void {
        door.crossings.forEach((crossing) => this.disposeCrossing(door, crossing));
        door.crossings.length = 0;
        // The particle texture is the shared glow halo, so it is kept.
        door.motes.dispose(false);
        door.material.dispose();
        door.mesh.dispose();
    }
}

function sameDoorway(a: HeldDoorway, b: HeldDoorway): boolean {
    return a.from.equals(b.from) && a.to.equals(b.to) && a.heightMeters === b.heightMeters && a.colorHex === b.colorHex;
}

/**
 * Where `node` is now. Its world matrix is recomputed outright: Babylon
 * caches it per render, so a crosser moved since this frame's render began
 * would otherwise read where it last was.
 */
function currentPosition(node: TransformNode): Vector3 {
    node.computeWorldMatrix(true);
    return node.absolutePosition;
}
